/**
 * PageBreadcrumb — spójny pasek nawigacji breadcrumb dla wszystkich podstron.
 * Automatycznie dodaje "Strona główna" jako pierwszy element.
 * Podstrona deklaruje pełną hierarchię, a SiteShell wyświetla ją pod nagłówkiem.
 * Nie dodawaj własnego paska ani pozycjonowania w szablonach podstron.
 *
 * Użycie:
 *   <PageBreadcrumb crumbs={[{ label: 'Kontakt' }]} />
 *   <PageBreadcrumb crumbs={[{ label: 'Blog', href: '/blog' }, { label: post.title }]} />
 */
import { createContext, useCallback, useContext, useId, useLayoutEffect, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { Link, useLocation } from 'wouter';
import { ChevronRight } from 'lucide-react';
import { canonicalPath } from '@workspace/url-source';
import { serializeCmsJson } from '@workspace/url-source/cms';
import { cmsPublicPath } from '@/cms/content';

const DARK   = '#2a1f14';
const MUTED  = 'rgba(42,31,20,0.50)';
const SITE_URL = 'https://bukietowa.pl';

export type BreadcrumbCrumb = {
  label: string;
  href?: string;
};

type BreadcrumbEntry = { id: string; path: string; crumbs: BreadcrumbCrumb[]; overlay: boolean };
type RegisterBreadcrumb = (entry: BreadcrumbEntry) => () => void;
const RegisterContext = createContext<RegisterBreadcrumb | null>(null);
const TrailContext = createContext<BreadcrumbEntry | null>(null);

/** Browser-generated product snapshots must match the first hydration render. */
function readBreadcrumbSnapshot(path: string): BreadcrumbEntry | null {
  if (typeof document === 'undefined') return null;
  const serialized = document.querySelector('#site-root [data-breadcrumb-snapshot]')
    ?.getAttribute('data-breadcrumb-snapshot');
  if (!serialized) return null;
  try {
    const snapshot = JSON.parse(serialized) as Partial<BreadcrumbEntry> | null;
    if (snapshot?.path !== path || typeof snapshot.overlay !== 'boolean' || !Array.isArray(snapshot.crumbs)) return null;
    if (!snapshot.crumbs.every(crumb => crumb && typeof crumb.label === 'string'
      && (crumb.href === undefined || typeof crumb.href === 'string'))) return null;
    return { id: 'snapshot', path, crumbs: snapshot.crumbs, overlay: snapshot.overlay };
  } catch {
    return null;
  }
}

/** Pages supply their own titles; the site shell owns the single visible trail. */
export function PageBreadcrumbProvider({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const [entry, setEntry] = useState<BreadcrumbEntry | null>(() => readBreadcrumbSnapshot(canonicalPath(location)));
  const register = useCallback<RegisterBreadcrumb>((next) => {
    setEntry(next);
    return () => setEntry(current => current?.id === next.id ? null : current);
  }, []);

  return (
    <RegisterContext.Provider value={register}>
      <TrailContext.Provider value={entry}>{children}</TrailContext.Provider>
    </RegisterContext.Provider>
  );
}

/** Keep the same position below the header on every route, including lazy pages. */
export function PageBreadcrumbOutlet({ top = 0 }: { top?: CSSProperties['top'] }) {
  const [location] = useLocation();
  const entry = useContext(TrailContext);
  const path = canonicalPath(location);
  if (path === '/') return null;

  // Never show the previous page's title while the next page is loading.
  const current = entry?.path === path ? entry : null;
  return <BreadcrumbTrail crumbs={current?.crumbs ?? null} currentPath={path} overlayTop={current?.overlay === false ? undefined : top} />;
}

/** "Strona główna" jest zawsze pierwszym elementem — dodawane tu, nie przez wywołującego. */
function withHome(crumbs: BreadcrumbCrumb[]): BreadcrumbCrumb[] {
  return [{ label: 'Strona główna', href: '/' }, ...crumbs];
}

function canonicalBreadcrumbPath(href: string): string {
  const pathname = new URL(href, SITE_URL).pathname;
  return canonicalPath(pathname);
}

function currentBreadcrumbPath(): string {
  if (typeof window === 'undefined') return '/';
  const basePrefix = import.meta.env.BASE_URL.replace(/\/$/, '');
  const raw = cmsPublicPath();
  const pathname = basePrefix && raw.startsWith(basePrefix)
    ? raw.slice(basePrefix.length) || '/'
    : raw;
  return canonicalPath(pathname);
}

function breadcrumbJsonLd(all: BreadcrumbCrumb[], currentPath: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: all.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.label,
      item: `${SITE_URL}${crumb.href ? canonicalBreadcrumbPath(crumb.href) : currentPath}`,
    })),
  };
}

/**
 * Emituje wyłącznie BreadcrumbList JSON-LD, bez wizualnego paska nawigacji.
 */
export function BreadcrumbJsonLd({ crumbs }: { crumbs: BreadcrumbCrumb[] }) {
  const jsonLd = breadcrumbJsonLd(withHome(crumbs), currentBreadcrumbPath());
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeCmsJson(jsonLd) }} />;
}

interface PageBreadcrumbProps {
  crumbs: BreadcrumbCrumb[];
  /** Use normal flow only on compact pages without a hero (e.g. product/checkout). */
  overlay?: boolean;
}

export function PageBreadcrumb({ crumbs, overlay = true }: PageBreadcrumbProps) {
  const register = useContext(RegisterContext);
  const [location] = useLocation();
  const id = useId();
  const path = canonicalPath(location);
  // Callers commonly pass inline arrays. Depend on their contents, not identity.
  const serialized = JSON.stringify(crumbs);
  useLayoutEffect(() => {
    return register?.({ id, path, crumbs: JSON.parse(serialized) as BreadcrumbCrumb[], overlay });
  }, [register, id, path, serialized, overlay]);

  return register ? null : <BreadcrumbTrail crumbs={crumbs} currentPath={path} />;
}

function BreadcrumbTrail({ crumbs, currentPath, overlayTop }: { crumbs: BreadcrumbCrumb[] | null; currentPath: string; overlayTop?: CSSProperties['top'] }) {
  const navRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const nav = navRef.current;
    const site = nav?.closest<HTMLElement>('#site-root');
    if (!nav || !site) return;
    const measure = () => site.style.setProperty('--page-breadcrumb-height', `${nav.getBoundingClientRect().height}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => {
      observer.disconnect();
      site.style.removeProperty('--page-breadcrumb-height');
    };
  }, []);
  const all = withHome(crumbs ?? []);
  const jsonLd = crumbs ? breadcrumbJsonLd(all, currentPath) : null;

  return (
    <>
    {jsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeCmsJson(jsonLd) }} />}
    <nav
      ref={navRef}
      aria-label="Breadcrumb"
      aria-busy={!crumbs}
      data-testid="page-breadcrumb"
      data-breadcrumb-snapshot={crumbs ? JSON.stringify({ path: currentPath, crumbs, overlay: overlayTop !== undefined }) : undefined}
      className="block"
      style={{
        padding: '22px 0 4px',
        minHeight: 44,
        background: 'transparent',
        ...(overlayTop !== undefined ? { position: 'absolute', top: overlayTop, left: 0, right: 0, zIndex: 10 } : {}),
      }}
    >
      <ol
        className="page-container"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          listStyle: 'none',
          lineHeight: '18px',
        }}
      >
        {all.map((crumb, i) => {
          const isLast = i === all.length - 1;
          return (
            <li key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
              {(!isLast || !crumbs) && crumb.href ? (
                <Link
                  href={canonicalBreadcrumbPath(crumb.href)}
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 11.5,
                    color: MUTED,
                    textDecoration: 'none',
                  }}
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 11.5,
                    color: isLast ? DARK : MUTED,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {crumb.label}
                </span>
              )}
              {!isLast && (
                <ChevronRight size={11} color={`${DARK}33`} aria-hidden="true" style={{ flexShrink: 0, alignSelf: 'center' }} />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
    </>
  );
}
